import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const initialForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  username: "",
  phone: "",
  gender: "",
  date_of_birth: "",
  address: "",
  department: "",
  job_title: "",
  qualification: "",
  employment_date: "",
  status: "ACTIVE",
  temporary_password: "",
};

const emptyAllocation = {
  subject_type: "SPECIALIST",
  subject: "",
  class_subject: "",
  class_groups: [],
  term: "",
  is_lead_teacher: false,
  is_active: true,
};

const teacherFlowOptions = [
  {
    value: "CLASS_TEACHER",
    label: "Class Teacher",
    icon: "bi-person-badge",
    description: "Usually responsible for one class group and normal class-teacher subjects.",
  },
  {
    value: "SPECIALIST",
    label: "Specialist Teacher",
    icon: "bi-cpu",
    description: "Teaches specialist subjects across many grades, streams or class groups.",
  },
  {
    value: "BOTH",
    label: "Class & Specialist",
    icon: "bi-intersect",
    description: "Can be a class teacher and also teach specialist subjects.",
  },
];

const getSubjectTypeLabel = (value) => {
  if (value === "SPECIALIST") return "Specialist Subject";
  if (value === "CLASS_TEACHER") return "Class Teacher Subject";
  return "Subject";
};

const uniqueRows = (rows, field) => [
  ...new Set(rows.map((row) => row[field]).filter(Boolean)),
];

const inferTeacherFlowFromAllocations = (rows = []) => {
  const hasSpecialist = rows.some((row) => row.subject_type === "SPECIALIST");
  const hasClassTeacher = rows.some((row) => row.subject_type === "CLASS_TEACHER");

  if (hasSpecialist && hasClassTeacher) return "BOTH";
  if (hasSpecialist) return "SPECIALIST";
  return "CLASS_TEACHER";
};


const getErrorMessage = (data, fallback) => {
  if (data?.non_field_errors?.[0]?.includes("unique set")) {
    return "This teacher already has that class-subject for the selected term.";
  }

  return (
    data?.non_field_errors?.[0] ||
    data?.detail ||
    Object.values(data || {})?.flat?.()?.[0] ||
    fallback
  );
};

const steps = [
  { key: "personal", label: "Personal Details", icon: "bi-person" },
  { key: "professional", label: "Professional Details", icon: "bi-briefcase" },
  { key: "allocation", label: "Teaching Allocation", icon: "bi-journal-bookmark" },
  { key: "login", label: "Login Details", icon: "bi-key" },
];

function TeacherRegisterModal({ show, onClose, onCreated }) {
  const [formData, setFormData] = useState(initialForm);
  const [allocationForm, setAllocationForm] = useState(emptyAllocation);
  const [allocationRows, setAllocationRows] = useState([]);
  const [teacherFlow, setTeacherFlow] = useState("SPECIALIST");
  const [classSubjects, setClassSubjects] = useState([]);
  const [terms, setTerms] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadAcademicSetup = async () => {
      if (!show) return;

      try {
        const [classSubjectsResponse, termsResponse] = await Promise.all([
          axiosInstance.get("academic/class-subjects/"),
          axiosInstance.get("academic/terms/"),
        ]);

        setClassSubjects(classSubjectsResponse.data || []);
        setTerms(termsResponse.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Could not load academic setup.");
      }
    };

    loadAcademicSetup();
  }, [show]);

  const visibleClassSubjects = useMemo(() => {
    if (teacherFlow === "SPECIALIST") {
      return classSubjects.filter((item) => item.subject_type === "SPECIALIST");
    }
    if (teacherFlow === "CLASS_TEACHER") {
      return classSubjects.filter((item) => item.subject_type === "CLASS_TEACHER");
    }
    return classSubjects;
  }, [classSubjects, teacherFlow]);

  const availableSubjects = useMemo(() => {
    const grouped = new Map();
    visibleClassSubjects.forEach((item) => {
      if (!grouped.has(item.subject)) {
        grouped.set(item.subject, {
          id: item.subject,
          name: item.subject_name,
          code: item.subject_code,
          type: item.subject_type,
          typeLabel: item.subject_type_label || getSubjectTypeLabel(item.subject_type),
        });
      }
    });
    return Array.from(grouped.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [visibleClassSubjects]);

  const matchingClassSubjects = useMemo(() => {
    if (!allocationForm.subject) return [];
    return visibleClassSubjects.filter((item) => String(item.subject) === String(allocationForm.subject));
  }, [visibleClassSubjects, allocationForm.subject]);

  const selectedClassSubject = useMemo(
    () =>
      classSubjects.find(
        (item) => String(item.id) === String(allocationForm.class_subject)
      ),
    [allocationForm.class_subject, classSubjects]
  );

  const selectedTerm = useMemo(
    () => terms.find((term) => String(term.id) === String(allocationForm.term)),
    [allocationForm.term, terms]
  );

  const isLastStep = activeStep === steps.length - 1;

  if (!show) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAllocationChange = (event) => {
    const { name, value, type, checked, selectedOptions } = event.target;

    if (name === "class_groups") {
      const selectedValues = Array.from(selectedOptions || []).map((option) => option.value);
      setAllocationForm((prev) => ({ ...prev, class_groups: selectedValues }));
      return;
    }

    setAllocationForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "subject_type") {
        next.subject = "";
        next.class_subject = "";
        next.class_groups = [];
      }
      if (name === "subject") {
        next.class_subject = "";
        next.class_groups = [];
      }
      return next;
    });
  };

  const addAllocationRow = () => {
    if (!allocationForm.term) {
      toast.warning("Select the term first.");
      return;
    }

    const selectedRows = [];

    const specialistAllocation =
      (teacherFlow === "SPECIALIST" || teacherFlow === "BOTH") &&
      allocationForm.subject_type !== "CLASS_TEACHER";

    if (specialistAllocation) {
      if (!allocationForm.subject) {
        toast.warning("Select the specialist subject first.");
        return;
      }
      if (!allocationForm.class_groups.length) {
        toast.warning("Select at least one class group for this specialist teacher.");
        return;
      }

      allocationForm.class_groups.forEach((classGroupId) => {
        const match = matchingClassSubjects.find(
          (item) => String(item.class_group) === String(classGroupId)
        );
        if (match) selectedRows.push(match);
      });
    } else {
      if (!allocationForm.class_subject) {
        toast.warning("Select the class subject first.");
        return;
      }
      if (selectedClassSubject) selectedRows.push(selectedClassSubject);
    }

    if (selectedRows.length === 0) {
      toast.warning("No matching class subjects were found. Create them in Academic Setup first.");
      return;
    }

    const rowsToAdd = [];
    selectedRows.forEach((classSubject) => {
      const duplicate = allocationRows.some(
        (row) =>
          String(row.class_subject) === String(classSubject.id) &&
          String(row.term) === String(allocationForm.term)
      );

      if (!duplicate) {
        rowsToAdd.push({
          class_subject: classSubject.id,
          term: allocationForm.term,
          is_lead_teacher: allocationForm.is_lead_teacher,
          is_active: allocationForm.is_active,
          class_group_name: classSubject.class_group_name || "",
          subject_name: classSubject.subject_name || "",
          subject_code: classSubject.subject_code || "",
          subject_type: classSubject.subject_type || "CLASS_TEACHER",
          subject_type_label: classSubject.subject_type_label || getSubjectTypeLabel(classSubject.subject_type),
          academic_year_name:
            classSubject.academic_year_name || selectedTerm?.academic_year_name || "",
          term_name: selectedTerm?.name || "",
        });
      }
    });

    if (rowsToAdd.length === 0) {
      toast.warning("Those allocations are already added.");
      return;
    }

    setAllocationRows((prev) => [...prev, ...rowsToAdd]);
    setAllocationForm(emptyAllocation);
  };

  const removeAllocationRow = (index) => {
    setAllocationRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setAllocationForm(emptyAllocation);
    setAllocationRows([]);
    setTeacherFlow("SPECIALIST");
    setActiveStep(0);
  };

  const validateStep = (stepIndex = activeStep) => {
    if (stepIndex === 0) {
      if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
        toast.warning("First name, last name and email are required.");
        return false;
      }
    }

    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const goBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = (index) => {
    if (index > activeStep && !validateStep()) return;
    setActiveStep(index);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStep(0)) return;

    try {
      setSaving(true);
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([, value]) => value !== "")
      );
      payload.subjects_taught = uniqueRows(allocationRows, "subject_name").join(", ");
      payload.classes_assigned = uniqueRows(allocationRows, "class_group_name").join(", ");

      const response = await axiosInstance.post("teachers/register/", payload);
      const createdTeacher = response.data?.teacher;
      const teacherId = createdTeacher?.id;

      if (teacherId && allocationRows.length > 0) {
        await Promise.all(
          allocationRows.map((allocation) =>
            axiosInstance.post("academic/teacher-allocations/", {
              teacher: teacherId,
              class_subject: allocation.class_subject,
              term: allocation.term,
              is_lead_teacher: allocation.is_lead_teacher,
              is_active: allocation.is_active,
            })
          )
        );
      }

      toast.success("Teacher registered successfully.");
      onCreated(createdTeacher);
      resetForm();
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      toast.error(getErrorMessage(data, "Failed to register teacher."));
    } finally {
      setSaving(false);
    }
  };

  const renderPersonal = () => (
    <div className="row g-3">
      <div className="col-md-4">
        <label className="form-label">First Name</label>
        <input name="first_name" className="form-control" value={formData.first_name} onChange={handleChange} required />
      </div>
      <div className="col-md-4">
        <label className="form-label">Middle Name</label>
        <input name="middle_name" className="form-control" value={formData.middle_name} onChange={handleChange} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Last Name</label>
        <input name="last_name" className="form-control" value={formData.last_name} onChange={handleChange} required />
      </div>
      <div className="col-md-6">
        <label className="form-label">Email</label>
        <input type="email" name="email" className="form-control" value={formData.email} onChange={handleChange} required />
      </div>
      <div className="col-md-6">
        <label className="form-label">Phone</label>
        <input name="phone" className="form-control" value={formData.phone} onChange={handleChange} />
      </div>
      <div className="col-md-6">
        <label className="form-label">Gender</label>
        <select name="gender" className="form-select" value={formData.gender} onChange={handleChange}>
          <option value="">Select</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label">Date of Birth</label>
        <input type="date" name="date_of_birth" className="form-control" value={formData.date_of_birth} onChange={handleChange} />
      </div>
      <div className="col-12">
        <label className="form-label">Address</label>
        <textarea name="address" rows="2" className="form-control" value={formData.address} onChange={handleChange}></textarea>
      </div>
    </div>
  );

  const renderProfessional = () => (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label fw-semibold">Teacher Type</label>
        <div className="row g-2">
          {teacherFlowOptions.map((option) => (
            <div className="col-md-4" key={option.value}>
              <button
                type="button"
                className={`btn w-100 h-100 text-start border ${teacherFlow === option.value ? "btn-success" : "btn-light"}`}
                onClick={() => {
                  setTeacherFlow(option.value);
                  setAllocationForm({
                    ...emptyAllocation,
                    subject_type: option.value === "CLASS_TEACHER" ? "CLASS_TEACHER" : "SPECIALIST",
                  });
                  setAllocationRows([]);
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className={`bi ${option.icon}`}></i>
                  <strong>{option.label}</strong>
                </div>
                <small className={teacherFlow === option.value ? "text-white-50" : "text-muted"}>
                  {option.description}
                </small>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="col-md-6">
        <label className="form-label">Department</label>
        <input name="department" className="form-control" placeholder="e.g. ICT, Sciences, Infant Department" value={formData.department} onChange={handleChange} />
      </div>
      <div className="col-md-6">
        <label className="form-label">Job Title</label>
        <input name="job_title" className="form-control" placeholder={teacherFlow === "SPECIALIST" ? "e.g. ICT & Robotics Specialist" : "e.g. Grade 3 Class Teacher"} value={formData.job_title} onChange={handleChange} />
      </div>
      <div className="col-md-6">
        <label className="form-label">Qualification</label>
        <input name="qualification" className="form-control" placeholder="e.g. Diploma in Education" value={formData.qualification} onChange={handleChange} />
      </div>
      <div className="col-md-6">
        <label className="form-label">Employment Date</label>
        <input type="date" name="employment_date" className="form-control" value={formData.employment_date} onChange={handleChange} />
      </div>
    </div>
  );

  const renderAllocation = () => {
    const specialistMode = teacherFlow === "SPECIALIST" || teacherFlow === "BOTH";

    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="alert alert-light border mb-0">
            <strong>{teacherFlowOptions.find((item) => item.value === teacherFlow)?.label}</strong>
            <div className="small text-muted">
              {specialistMode
                ? "Choose one specialist subject, then select all class groups/streams taught by this teacher. The system will create the class-subject allocations in bulk."
                : "Choose the class-teacher subject allocation for the class group. Main class teacher responsibility is still controlled from Academic Setup > Class Groups."}
            </div>
          </div>
        </div>

        {teacherFlow === "BOTH" && (
          <div className="col-md-4">
            <label className="form-label">Allocation Type</label>
            <select name="subject_type" className="form-select" value={allocationForm.subject_type} onChange={handleAllocationChange}>
              <option value="SPECIALIST">Specialist Subject</option>
              <option value="CLASS_TEACHER">Class Teacher Subject</option>
            </select>
          </div>
        )}

        {specialistMode && allocationForm.subject_type !== "CLASS_TEACHER" ? (
          <>
            <div className="col-md-4">
              <label className="form-label">Specialist Subject</label>
              <select name="subject" className="form-select" value={allocationForm.subject} onChange={handleAllocationChange}>
                <option value="">Select subject</option>
                {availableSubjects.filter((subject) => subject.type === "SPECIALIST").map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.code ? `${subject.code} - ` : ""}{subject.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Class Groups / Streams</label>
              <select
                name="class_groups"
                className="form-select"
                multiple
                size="6"
                value={allocationForm.class_groups}
                onChange={handleAllocationChange}
                disabled={!allocationForm.subject}
              >
                {matchingClassSubjects.map((item) => (
                  <option key={item.id} value={item.class_group}>
                    {item.academic_year_name} - {item.class_group_name}
                  </option>
                ))}
              </select>
              <small className="text-muted">Hold Ctrl to select many class groups.</small>
            </div>
          </>
        ) : (
          <div className="col-md-5">
            <label className="form-label">Class Subject</label>
            <select name="class_subject" className="form-select" value={allocationForm.class_subject} onChange={handleAllocationChange}>
              <option value="">Select class subject</option>
              {visibleClassSubjects.filter((item) => item.subject_type === "CLASS_TEACHER").map((item) => (
                <option key={item.id} value={item.id}>
                  {item.academic_year_name} - {item.class_group_name} - {item.subject_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="col-md-3">
          <label className="form-label">Term</label>
          <select name="term" className="form-select" value={allocationForm.term} onChange={handleAllocationChange}>
            <option value="">Select term</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>
                {term.academic_year_name} - {term.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <label className="form-check mb-2">
            <input type="checkbox" name="is_lead_teacher" className="form-check-input" checked={allocationForm.is_lead_teacher} onChange={handleAllocationChange} />
            <span className="form-check-label">Lead</span>
          </label>
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button type="button" className="btn btn-outline-success w-100" onClick={addAllocationRow}>
            Add
          </button>
        </div>

        <div className="col-12">
          <div className="table-responsive border rounded-3">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Term</th>
                  <th>Lead</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {allocationRows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      No allocations added yet.
                    </td>
                  </tr>
                ) : (
                  allocationRows.map((allocation, index) => (
                    <tr key={`${allocation.class_subject}-${allocation.term}`}>
                      <td>{allocation.class_group_name}</td>
                      <td>{allocation.subject_code ? `${allocation.subject_code} - ` : ""}{allocation.subject_name}</td>
                      <td>
                        <span className={`badge ${allocation.subject_type === "SPECIALIST" ? "text-bg-info" : "text-bg-light border text-dark"}`}>
                          {allocation.subject_type_label || getSubjectTypeLabel(allocation.subject_type)}
                        </span>
                      </td>
                      <td>{allocation.academic_year_name} - {allocation.term_name}</td>
                      <td>{allocation.is_lead_teacher ? "Yes" : "No"}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-light border" title="Remove allocation" onClick={() => removeAllocationRow(index)}>
                          <i className="bi bi-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderLogin = () => (
    <div className="row g-3">
      <div className="col-12">
        <div className="alert alert-success border-0">
          Staff number will be generated automatically. Teacher can login using email or username.
        </div>
      </div>
      <div className="col-md-4">
        <label className="form-label">Username Optional</label>
        <input name="username" className="form-control" placeholder="Leave blank to use staff number" value={formData.username} onChange={handleChange} />
      </div>
      <div className="col-md-4">
        <label className="form-label">Status</label>
        <select name="status" className="form-select" value={formData.status} onChange={handleChange}>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending Approval</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Temporary Password Optional</label>
        <input name="temporary_password" className="form-control" placeholder="Leave blank to auto-generate" value={formData.temporary_password} onChange={handleChange} />
      </div>
    </div>
  );

  const renderStep = () => {
    if (activeStep === 0) return renderPersonal();
    if (activeStep === 1) return renderProfessional();
    if (activeStep === 2) return renderAllocation();
    return renderLogin();
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Register Teacher</h5>
              <small className="text-muted">Create staff profile, allocations, and login details.</small>
            </div>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-3 pt-3">
              <div className="row g-2">
                {steps.map((step, index) => {
                  const isActive = activeStep === index;
                  const isComplete = activeStep > index;
                  return (
                    <div className="col-md-3" key={step.key}>
                      <button
                        type="button"
                        className={`btn w-100 text-start d-flex align-items-center gap-2 border ${isActive ? "text-white" : "btn-light"}`}
                        style={isActive ? { background: "#125b2d", borderColor: "#125b2d" } : {}}
                        onClick={() => goToStep(index)}
                      >
                        <span className={`rounded-circle d-flex align-items-center justify-content-center ${isActive ? "bg-white text-success" : "bg-success text-white"}`} style={{ width: 28, height: 28, flexShrink: 0 }}>
                          {isComplete ? <i className="bi bi-check"></i> : index + 1}
                        </span>
                        <span>
                          <span className="d-block fw-semibold">{step.label}</span>
                          <small className={isActive ? "text-white-50" : "text-muted"}>
                            <i className={`bi ${step.icon} me-1`}></i>
                            Step {index + 1}
                          </small>
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-body" style={{ maxHeight: "62vh", overflowY: "auto" }}>
              {renderStep()}
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button type="button" className="btn btn-light" onClick={handleClose}>
                Cancel
              </button>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-success" onClick={goBack} disabled={activeStep === 0 || saving}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
                {!isLastStep ? (
                  <button type="button" className="btn btn-success" onClick={goNext} disabled={saving}>
                    Next
                    <i className="bi bi-arrow-right ms-2"></i>
                  </button>
                ) : (
                  <button type="submit" className="btn btn-success" disabled={saving}>
                    {saving ? "Saving..." : "Register Teacher"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TeacherRegisterModal;
